import { SelectionModel } from "@angular/cdk/collections";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { CustomErrorStateMatcher } from "@comunicare/comunicare-angular-forms-validator";
import { ConfirmationDialogComponent, ConfirmationDialogType, GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import { FileLogger, Tools } from "@comunicare/comunicare-helper";
import {
  DataType,
  IFilter,
  IObservationInfo,
  IPatientsGroup,
  IPatientsListInfo,
  IPatientUser,
  IPreferenceUser,
  ITableParameter,
  IWidgetPatientListParameter,
  PatientDataInfoPeriod,
  PatientsGroup,
  PreferenceContext,
  USER_STATUS,
  WidgetName,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { concat, fromEvent, merge, Subject, Subscription, zip } from "rxjs";
import { debounceTime, distinctUntilChanged, first, last, skipWhile, takeUntil, tap } from "rxjs/operators";
import { AddToGroupDialogComponent } from "src/app/components/add-to-group-dialog/add-to-group-dialog.component";
import { Item } from "src/app/components/item-selector/item-selector.component";
import { PatientDisableDialogComponent } from "src/app/components/patient-infos/patient-disable-dialog/patient-disable-dialog.component";
import { Base64Helper } from "src/app/helpers/Base64Helper";
import { HelpData } from "src/app/helpers/helpData";
import { MatriculeInsHelper } from "src/app/helpers/matriculeInsHelper";
import { PatientDataSource } from "src/app/pages/patient-list-page/patient-list/patient.datasource";
import { AuthService } from "src/app/providers/auth.service";
import { CommunicationsCrudService } from "src/app/providers/communications-crud.service";
import { GroupService } from "src/app/providers/group-api.service";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { ObservationsService } from "src/app/providers/observations.service";
import { PatientService } from "src/app/providers/patient.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { QRService } from "src/app/providers/qr-api.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { ResponsiveService } from "src/app/providers/responsive.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { UserStatisticsService } from "src/app/providers/userStatistics.service";

@Component({
  selector: "app-patient-list",
  templateUrl: "./patient-list.component.html",
  styleUrls: ["./patient-list.component.scss", "../../../../assets/comunicare-list.scss"],
})
export class PatientListComponent implements AfterViewInit, OnInit, OnDestroy {
  /**
   * Material table
   */
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatTable) table: MatTable<IPatientUser>;
  @ViewChild("searchInput") searchInput: ElementRef; // input mot clé dans le html
  @ViewChild("searchClearBtn", { read: ElementRef }) searchClearBtn: ElementRef;

  public searchINS: ElementRef;
  public insSearchSubscription: Subscription;
  @ViewChild("searchINS") set input(input: ElementRef) {
    if (input) {
      this.insSearchSubscription?.unsubscribe();
      // initially setter gets called with undefined
      this.searchINS = input;
      const searchINSEvent$ = fromEvent(this.searchINS.nativeElement, "keyup");
      this.insSearchSubscription = searchINSEvent$
        .pipe(
          takeUntil(this.onDestroy$),
          debounceTime(150),
          distinctUntilChanged(),
          tap(() => {
            if (!this.formGroup.get("ins").value) {
              // reset the patient list without ins filtering
              this.computePatientsList();
            }
          })
        )
        .subscribe();
    }
  }

  @Input() isSlimTable: boolean;

  private preferences: ITableParameter & { patientDataPeriod?: PatientDataInfoPeriod; patientOpenRows?: string[] };
  public dataSource: PatientDataSource;
  public patientsCount: number;
  public currentPageSize: number;
  public currentPage: number;
  public actionOnMultiple = false;
  public filters: IFilter[] = [];
  /** Raw active filters, kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public activeFilters: IFilter[] = [];
  public currentServices: string[] = [];
  private monitoringServices: string[];

  // Columns
  public availableColumnItems: Item[] = [];
  public displayedColumns: string[] = [];
  public availablePatientDataPeriods: Item[] = [];
  public patientDataPeriod: PatientDataInfoPeriod;
  public PatientDataPeriodEnum = PatientDataInfoPeriod;
  private patientOpenRows: string[] = [];

  /**
   * IFilter
   */
  public dataTypeText = DataType.TEXT;
  public dataTypeDate = DataType.DATE;
  public dataTypeChoice = DataType.CHOICE;
  public globalSearchValue = "";

  private withInactive = true;

  public groups: PatientsGroup[];
  public formGroup: UntypedFormGroup;
  public groupId: string;
  public noGroup: PatientsGroup = {
    caremateIdentifier: undefined,
    _id: "no-group",
    entityStatus: undefined,
    groupName: "no-group",
    modified: undefined,
    patients: [],
    public: undefined,
    healthcareservice: undefined,
    isValid: undefined,
  };

  public FRANCE: string = this.sessionService.FRANCE;
  public country: string;

  public selection = new SelectionModel<IPatientsListInfo>(true, []);
  /** Selection contains at least one active patient */
  public hasActiveSelected = false;
  /** Selection contains at least one deactivated (temporarily or permanently) patient */
  public hasInactiveSelected = false;
  public matcher = new CustomErrorStateMatcher();
  public isSelection = false;

  public isAllServices = true;
  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();

  public forceSelected = false;

  private firstInit = true;

  public expandedElements: IPatientsListInfo[] = [];
  public patientsObservations: {
    [caremateId: string]: {
      observations: IObservationInfo[];
      isLoading: boolean;
    };
  } = {};
  public patientsNbObservations: {
    [caremateId: string]: {
      count: number;
      isLoading: boolean;
    };
  } = {};
  public patientsNbQuestionnaires: {
    [caremateId: string]: {
      count: number;
      isLoading: boolean;
    };
  } = {};

  public referenceCountry = "";

  constructor(
    private patientService: PatientService,
    private translateService: TranslateService,
    private authService: AuthService,
    private responsiveDialog: ResponsiveDialogService,
    private dialog: MatDialog,
    private preferenceService: PreferenceService,
    private sessionService: SessionService,
    private snackBar: MatSnackBar,
    private router: Router,
    private userService: UserService,
    private healthcareService: HealthcareserviceService,
    public helpData: HelpData,
    public groupService: GroupService,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private crud: CommunicationsCrudService,
    private statService: UserStatisticsService,
    public responsiveService: ResponsiveService,
    private observationsService: ObservationsService,
    private qrService: QRService
  ) {
    // get data from resolvers
    this.preferences = this.route.snapshot.data.preferences;
    this.filters = this.preferences?.filters ? this.preferences.filters : [];
    this.currentPageSize = this.preferences?.itemsPerPage ? this.preferences.itemsPerPage : 25;
    this.patientsCount = this.route.snapshot.data.patientsCount.count;
    this.currentPage = this.sessionService.pageIndex;
    this.formGroup = this.fb.group({
      groupFilter: [""],
      ins: ["", MatriculeInsHelper.insValidator],
    });
    this.setupServices();
  }

  ngOnInit(): void {
    this.setupColumns();
    if (!this.userService.isAuthorizedSync(null, "dashboard/ins", "GET")) {
      this.formGroup.get("ins")?.disable();
    }

    this.referenceCountry = this.sessionService.account?.referenceCountry;

    this.selection.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.hasInactiveSelected = this.selection.selected.some((p) => this.isDeactivated(p));
      this.hasActiveSelected = this.selection.selected.some((p) => !this.isDeactivated(p));
    });

    // Create DataSource and first patients loading
    this.dataSource = new PatientDataSource(this.patientService);
    // apply filter
    this.filters?.forEach((filter) => this.dataSource.setFilter(filter));
    this.activeFilters = [...this.dataSource.getAllFilters()];
    this.dataSource.loading$.pipe(takeUntil(this.onDestroy$)).subscribe((isLoading) => {
      if (!isLoading) {
        this.setupObservations();
        const element = [...this.dataSource.getData()]; // create new array to avoid modifying the datasource
        if (
          Tools.isDefined(this.patientDataPeriod) &&
          (this.displayedColumns.includes("nbQuestionnaires") || this.displayedColumns.includes("nbObservations"))
        ) {
          this.setPatientDataCount(element.map((e) => e.caremateIdentifier));
        }
      }
    });
  }

  async ngAfterViewInit(): Promise<void> {
    this.currentPageSize = this.paginator.pageSize;

    if (
      this.sessionService.currentService &&
      this.userService.ownOrganization &&
      (this.healthcareService.availableServices()?.length > 0 || this.healthcareService.availableMonitoringServices().length > 0)
    ) {
      await this.setupServicesWatch();
    } else {
      this.sessionService.servicesSetupWatch
        .pipe(
          skipWhile(() => !this.userService.ownOrganization),
          first()
        )
        .subscribe(() => {
          this.setupServicesWatch();
        });
    }

    // server-side search
    const searchEvent$ = fromEvent(this.searchInput.nativeElement, "keyup");
    const searchResetEvent$ = fromEvent(this.searchClearBtn.nativeElement, "click");

    this.sessionService.refreshPatientDataList.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.computePatientsList();
    });

    this.sessionService.refreshServerTraductions.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.computePatientsList();
    });

    merge(searchEvent$, searchResetEvent$, this.responsiveService.isHandset$)
      .pipe(takeUntil(this.onDestroy$), debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        this.paginator.pageIndex = 0;
        this.computePatientsList();
      });

    if (this.sort && this.preferences?.sortId) {
      this.sort.active = this.preferences.sortId;
      this.sort.direction = this.preferences.sortOrder ?? "";
      this.sort.sortChange.next({ active: this.sort.active, direction: this.sort.direction });
    }

    // reset the paginator after sorting
    this.sort?.sortChange.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.paginator.pageIndex = 0;
      this.resetSelection();
      this.updatePreference();
      this.loadPatients();
    });

    // Detect group change and refresh patients list

    this.formGroup
      .get("groupFilter")
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((v) => {
        this.groupId = v?._id;
        this.resetSelection();
        this.updatePreference().then(() => {
          this.computePatientsList();
        });
      });

    // Detect page size change
    this.paginator.page.pipe(takeUntil(this.onDestroy$)).subscribe((page: PageEvent) => {
      this.sessionService.pageIndex = page.pageIndex;
      this.currentPageSize = this.paginator.pageSize;
      this.updatePreference().then(() => {
        this.resetSelection();
        this.loadPatients();
      });
    });

    this.sessionService.needRefreshPatientDataList();
  }

  ngOnDestroy(): void {
    this.updatePatientWidgetPreferences().pipe(last()).subscribe();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private async init(firstInit = false): Promise<void> {
    this.firstInit = false;
    if (this.userService.isMonitoringUser) {
      if (!this.sessionService.currentMonitoringService) {
        this.monitoringServices = [];
      } else if (this.sessionService.currentMonitoringService.reference === this.sessionService.allsOption) {
        this.monitoringServices = this.healthcareService.availableMonitoringServices()?.map((s) => s.asReference.reference);
      } else {
        this.monitoringServices = [this.sessionService.currentMonitoringService?.reference];
      }
    }

    if (
      !this.sessionService.currentService ||
      !this.healthcareService.availableServices() ||
      this.healthcareService.availableServices().length < 1
    ) {
      this.patientsCount = 0;
      this.paginator.pageIndex = 0;
      this.currentServices = [];
      await this.reloadGroups();
      this.loadPatients();
      return;
    }
    if (!this.sessionService.currentService) {
      this.currentServices = [];
    } else if (this.sessionService.currentService.reference === this.sessionService.allsOption) {
      this.currentServices = this.healthcareService.availableServices().map((s) => s.asReference.reference);
    } else {
      this.currentServices = [this.sessionService.currentService?.reference];
    }

    // if service has changed, reset the selected group which will trigger patients refresh; else : refresh patients
    if (firstInit) {
      await this.reloadGroups(this.preferences?.group);
    } else {
      await this.reloadGroups();
    }
  }

  private async setupServicesWatch(): Promise<void> {
    this.setupServices();
    this.setupColumns();
    await this.init(this.firstInit);
    if (this.userService.isMonitoringUser) {
      this.sessionService.refreshCurrentMonitService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        if (this.sessionService.currentMonitoringService) {
          this.setupServices();
          this.setupColumns();
          this.init(this.firstInit);
        }
      });
    }
    this.sessionService.refreshCurrentService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      if (this.sessionService.currentService) {
        this.setupServices();
        this.setupColumns();
        // trigger @Input setter on filtersDisplay comp. to allow filter name translation for french services (CMATE-5279)
        this.filters = [...this.filters];
        this.init(this.firstInit);
      }
    });
  }

  private setupServices() {
    if (
      (!this.userService.isMonitoringUser && !this.sessionService.currentService) ||
      (this.userService.isMonitoringUser && !this.sessionService.currentMonitoringService)
    ) {
      this.country = this.sessionService.currentCountry;
      this.isAllServices = true;
      return;
    }
    this.isAllServices = this.sessionService.isAllServices(this.userService.isMonitoringUser);
    this.country = this.sessionService.currentCountry;
  }

  /**
   * Columns must be set up everytime the service is updated because the translation of the columns names depends on the service country
   */
  private setupColumns() {
    this.initColumn();
    // remove unexisting column saved in preferences
    if (this.preferences?.availableColumnItems) {
      this.preferences.availableColumnItems.forEach((columun, index) => {
        const exist = this.availableColumnItems.find((c) => c.value === columun.value);
        if (!exist) {
          delete this.preferences.availableColumnItems[index];
        }
      });
      // add unexisting column saved in preferences
      this.availableColumnItems.find((column) => {
        const exist = this.preferences.availableColumnItems.find((c) => c?.value === column?.value);
        if (!exist) {
          this.preferences.availableColumnItems.push(column);
        }
      });
      // Restore column order + visibility
      this.preferences.availableColumnItems = this.preferences.availableColumnItems.filter((v) => v);

      // get correct display in case the service country is not the same
      this.availableColumnItems = this.preferences.availableColumnItems.map((item: Item) => {
        const matchingItem = this.availableColumnItems.find((i) => i.value === item.value);
        if (matchingItem) {
          item.display = matchingItem.display;
        }
        return item;
      });
    }
    this.setDisplayedColumns();
  }

  private computePatientsList(insNumber?: string) {
    if (!this.currentServices || this.currentServices.length < 1) {
      this.patientsCount = 0;
      this.paginator.pageIndex = 0;
      this.loadPatients();
      return;
    }

    const filtersToApply = this.responsiveService.isHandsetSubject.value ? [] : this.getFilters();

    this.patientService
      .getPatientsCount(
        this.currentServices,
        this.searchInput.nativeElement.value,
        filtersToApply,
        this.withInactive,
        this.groupId,
        this.sessionService.globalPref?.inactifPatientDuration,
        this.monitoringServices,
        insNumber
      )
      .pipe(first())
      .subscribe((result) => {
        this.patientsCount = result?.count;
        this.paginator.pageIndex = 0;
        this.loadPatients(insNumber);
      });
  }

  private loadPatients(insNumber?: string) {
    const filtersToApply = this.responsiveService.isHandsetSubject.value ? [] : this.getFilters();

    this.dataSource.loadData({
      sortId: this.sort?.active,
      sortOrder: this.sort?.direction,
      pageNumber: this.paginator.pageIndex,
      pageSize: this.paginator.pageSize,
      filters: !insNumber ? filtersToApply : [], // no need for other filters if we search by insNumber
      search: !insNumber ? this.searchInput.nativeElement.value : "",
      withInactive: this.withInactive,
      groupId: this.groupId,
      services: this.currentServices,
      monitoringServicesIds: this.monitoringServices,
      lang: this.sessionService.userLang,
      period: this.sessionService.globalPref?.inactifPatientDuration,
      insNumber,
    });
  }

  private initColumn(): void {
    if (this.isSlimTable) {
      // Define all available columns
      this.availableColumnItems = [
        {
          value: "status",
          display: "model.patient.status",
          checked: true,
        },
        {
          value: "name",
          display: this.country === this.FRANCE ? "forms.FrName" : "model.patient.name",
          checked: true,
        },
        {
          value: "firstname",
          display: this.country === this.FRANCE ? "forms.FrFirstname" : "model.patient.firstname",
          checked: true,
        },
        {
          value: "pseudoname",
          display: "model.patient.pseudoname",
          checked: true,
        },
        {
          value: "birthday",
          display: "model.patient.birthdate",
          checked: true,
        },
        {
          value: "gender",
          display: "model.patient.gender",
          checked: true,
        },
        {
          value: "lastActivity",
          display: "model.patient.lastActivity",
          checked: true,
        },
      ];
    } else {
      // Define all available columns
      this.availableColumnItems = [
        {
          value: "status",
          display: "model.patient.status",
          checked: true,
        },
        {
          value: "name",
          display: this.country === this.FRANCE ? "forms.FrName" : "model.patient.name",
          checked: true,
        },
        {
          value: "useName",
          display: "forms.useName",
          checked: false,
        },
        {
          value: "firstname",
          display: this.country === this.FRANCE ? "forms.FrFirstname" : "model.patient.firstname",
          checked: true,
        },
        {
          value: "useFirstname",
          display: "forms.useFirstname",
          checked: false,
        },
        {
          value: "pseudoname",
          display: "model.patient.pseudoname",
          checked: false,
        },
        {
          value: "birthday",
          display: "model.patient.birthdate",
          checked: true,
        },
        {
          value: "phone",
          display: "model.patient.phone",
          checked: true,
        },
        {
          value: "email",
          display: "model.patient.email",
          checked: false,
        },
        {
          value: "gender",
          display: "model.patient.gender",
          checked: true,
        },
        {
          value: "creation",
          display: "model.patient.creation",
          checked: true,
        },
        {
          value: "healthcare",
          display: "model.patient.healthcare",
          checked: false,
        },
        {
          value: "managingOrganization",
          display: "model.patient.organization",
          checked: false,
        },
        {
          value: "lastActivity",
          display: "model.patient.lastActivity",
          checked: true,
        },
        // { value: 'nationalNumber', display: trans['model.patient.nationalNumber'], checked: false },
        // see CMATE-1313
        {
          value: "careplan",
          display: "model.patient.careplan",
          checked: false,
        },
        {
          value: "careProvider",
          display: "model.patient.careProvider",
          checked: true,
        },
        {
          value: "insurance",
          display: "model.patient.insurance",
          checked: false,
        },
        {
          value: "monitService",
          display: "model.patient.monitService",
          checked: false,
        },
        {
          value: "monitPractitionersInfo",
          display: "forms.personInCharge",
          checked: false,
        },
        {
          value: "internalID",
          display: "table.internalId",
          checked: false,
        },
        // { value: 'nationalNumber', display: trans['model.patient.nationalNumber'], checked: false }
        // see CMATE-1313
      ];

      if (this.userService.isAuthorizedSync(null, "dashboard/observationsCount", "GET")) {
        this.availableColumnItems.push({
          value: "nbObservations",
          display: "tile.observations",
          checked: false,
        });
      }

      if (this.userService.isAuthorizedSync(null, "dashboard/questionnairesCount", "GET")) {
        this.availableColumnItems.push({
          value: "nbQuestionnaires",
          display: "tile.questionnaires",
          checked: false,
        });
      }
    }
  }

  public setDisplayedColumns(): void {
    if (this.userService.isAuthorizedSync(null, "dashboard/observationsInfos", "GET") && Tools.isDefined(this.patientDataPeriod)) {
      this.displayedColumns = this.actionOnMultiple
        ? ["expand", "select", ...this.availableColumnItems.filter((item) => item.checked).map((item) => item.value)]
        : ["expand", ...this.availableColumnItems.filter((item) => item.checked).map((item) => item.value)];
    } else {
      this.displayedColumns = this.actionOnMultiple
        ? ["select", ...this.availableColumnItems.filter((item) => item.checked).map((item) => item.value)]
        : [...this.availableColumnItems.filter((item) => item.checked).map((item) => item.value)];
    }
  }

  public async availableColumnChanged(items: Item[]): Promise<void> {
    if (
      (!this.displayedColumns.includes("nbObservations") && items.find((i) => i.value === "nbObservations")?.checked) ||
      (!this.displayedColumns.includes("nbQuestionnaires") && items.find((i) => i.value === "nbQuestionnaires")?.checked)
    ) {
      this.setDisplayedColumns();

      // reload to get the count of observations / questionnaires
      const element = [...this.dataSource.getData()]; // create new array to avoid modifying the datasource
      if (Tools.isDefined(this.patientDataPeriod)) {
        this.setPatientDataCount(element.map((e) => e.caremateIdentifier));
      }
      await this.updatePreference();
      return;
    }
    this.setDisplayedColumns();
    await this.updatePreference();
  }

  /**
   * Row interactions
   */
  public patientRowClicked(patient: IPatientsListInfo): void {
    if (this.actionOnMultiple) {
      this.selection.toggle(patient);
    } else if (this.userService.isAuthorizedSync(null, "/patient", "GET")) {
      this.sessionService.patientPageFrom = "/patients";
      const b64Id = Base64Helper.utf8_to_b64(patient.caremateIdentifier);
      this.router.navigateByUrl(`/patient;id=${b64Id}`, {
        state: {
          patient,
          widget: WidgetName.PATIENT_LIST,
        },
      });
    }
  }

  /**
   * Drag n'Drop
   */
  public async columnDropped(event: CdkDragDrop<string[]>): Promise<void> {
    const realIndexFrom =
      this.availableColumnItems.findIndex((item) => this.displayedColumns[event.previousIndex] === item.value) +
      (this.actionOnMultiple ? 1 : 0);
    const realIndexTo =
      this.availableColumnItems.findIndex((item) => this.displayedColumns[event.currentIndex] === item.value) +
      (this.actionOnMultiple ? 1 : 0);
    moveItemInArray(this.availableColumnItems, realIndexFrom, realIndexTo);
    this.setDisplayedColumns();
    await this.updatePreference();
  }

  /**
   * IFilter
   */
  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  public async applyFilter(filter: IFilter, isInit = false): Promise<void> {
    this.selection.clear();
    this.forceSelected = false;
    this.dataSource.setFilter(filter);
    this.activeFilters = [...this.dataSource.getAllFilters()];
    this.statService.createStatEvent("Used filter on patients list (" + filter.propertyName + ")");
    // only reinit pageIndex if not init filiter
    if (!isInit) {
      this.sessionService.pageIndex = this.paginator.pageIndex;
    }
    await this.updatePreference();
    this.sessionService.needRefreshPatientDataList();
  }

  public async clearFilter(): Promise<void> {
    if (this.sort) {
      this.sort.active = "name";
      this.sort.direction = "asc";
    }

    this.searchInput.nativeElement.value = "";
    this.dataSource.clearFilter();
    this.paginator.pageIndex = 0;
    this.isSelection = false;
    this.filters = [];
    this.activeFilters = [];
    await this.updatePreference();
    this.sessionService.needRefreshPatientDataList();
  }

  private getFilters(): IFilter[] {
    return (this.filters = [...this.dataSource.getAllFilters()]);
  }
  /**
   * Selection
   */
  public isAllSelected(): boolean {
    return this.selection.selected.length === this.dataSource.getData().length;
  }
  public masterToggle(): void {
    this.dataSource
      .connect(this.table)
      .pipe(first())
      .subscribe((rows) => {
        if (this.selection.hasValue() || this.forceSelected) {
          this.selection.clear();
          this.forceSelected = false;
        } else {
          this.selection.select(...rows);
        }
      });
  }

  /**
   * A temporary deactivation leaves the user status untouched: only the patient record is set
   * to inactive, so the status alone does not tell a temporarily deactivated patient apart.
   */
  private isDeactivated(patient: IPatientsListInfo): boolean {
    return patient.status === USER_STATUS.INACTIVE || patient.active === false;
  }

  // Actions
  public disableSelected(): void {
    let patientUsers: IPatientUser[] = [];

    this.getPatientUsers(this.selection.selected).then(
      (pu: IPatientUser[]) => {
        patientUsers = pu;
        this.dialog
          .open(PatientDisableDialogComponent, {
            data: { patients: patientUsers },
            disableClose: true,
          })
          .afterClosed()
          .pipe(takeUntil(this.onDestroy$))
          .subscribe(() => {
            this.resetSelection();
            this.sessionService.needRefreshPatientDataList();
          });
      },
      (error) => {
        FileLogger.error("PatientListPageComponent", "disableSelected", error);
      }
    );
  }

  public activateSelected(): void {
    this.translateService
      .get(
        this.selection.selected.length > 1 ? "page.patientlist.activatePatientPluralConfirm" : "page.patientlist.activatePatientConfirm",
        { length: this.selection.selected.length }
      )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((trans: string) => {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
          data: { message: trans, type: ConfirmationDialogType.CONFIRM },
        });

        dialogRef.afterClosed().subscribe(async (yes) => {
          if (!yes) {
            return;
          }
          this.getPatientUsers(this.selection.selected).then(
            async (pu: IPatientUser[]) => {
              pu.forEach(async (patientUser) => {
                await this.patientService.activate(patientUser.patient).pipe(first()).toPromise();
              });
            },
            (error) => {
              FileLogger.error("PatientListPageComponent", "activateSelected", error);
            }
          );

          this.resetSelection();
          this.sessionService.needRefreshPatientDataList();
        });
      });
  }

  public resetPasswordSelected(): void {
    const disableObs = this.selection.selected.map((patient) => {
      return this.authService.resetPassword(patient.caremateIdentifier);
    });
    zip(...disableObs)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.selection.clear();
        this.translateService
          .get("common.success")
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((trans) => {
            this.snackBar.open(trans, "ok", { duration: 3000 });
          });
      });
  }

  /**
   * Preferences
   */
  public updatePreference(): Promise<void> {
    return this.preferenceService
      .update({
        context: PreferenceContext.PATIENT_LIST,
        parameters: {
          filters: this.dataSource?.getAllFilters() ?? [],
          availableColumnItems: this.availableColumnItems,
          itemsPerPage: this.paginator.pageSize,
          group: this.formGroup.get("groupFilter").value,
          sortId: this.sort.active,
          sortOrder: this.sort.direction,
          patientDataPeriod: this.patientDataPeriod,
          patientOpenRows: this.patientsObservations ? Object.keys(this.patientsObservations) : undefined,
        } as ITableParameter & { patientDataPeriod?: PatientDataInfoPeriod; patientOpenRows?: string[] },
      })
      .pipe(first())
      .toPromise()
      .then((parameters: IPreferenceUser) => {
        const param = parameters.preferences.find((p) => p.context === PreferenceContext.PATIENT_LIST);
        this.preferences = param.parameters;
      });
  }

  private updatePatientWidgetPreferences() {
    // update also the patient list widget for get the sorted list of the page
    return this.preferenceService.update({
      context: PreferenceContext.WIDGET_PATIENT_LIST,
      parameters: {
        filters: this.filters,
        patientsCount: this.patientsCount,
        search: this.searchInput.nativeElement.value,
        groupId: this.groupId,
      } as IWidgetPatientListParameter,
    });
  }

  public activateCheckBox(): void {
    this.actionOnMultiple = !this.actionOnMultiple;
    if (this.actionOnMultiple) {
      // this.dataSource.clearAutoRefresh();
      this.displayedColumns.splice(this.displayedColumns[0] === "expand" ? 1 : 0, 0, "select");
    } else {
      this.resetSelection();
    }
  }

  public getCareProvidersDisplay(patient: IPatientsListInfo): string[] {
    return patient.careProvider.map((c) => c?.display);
  }

  public openPatientsHelp(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: { slides: this.helpData.patientListPageHelp },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }

  public async addToGroup(): Promise<void> {
    let list: IPatientUser[] = [];
    this.getPatientUsers(this.selection.selected).then(
      (pu: IPatientUser[]) => {
        list = pu;
        this.dialog
          .open(AddToGroupDialogComponent, {
            data: { list },
            disableClose: true,
          })
          .afterClosed()
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((res) => {
            if (res) {
              this.reloadGroups();
              this.sessionService.needRefreshPatientDataList();
              this.resetSelection();
            }
          });
      },
      (error) => {
        FileLogger.error("PatientListPageComponent", "addToGroup", error);
      }
    );
  }

  public async removeFromGroup(): Promise<void> {
    let list: IPatientUser[] = [];
    this.getPatientUsers(this.selection.selected).then(
      (pu: IPatientUser[]) => {
        list = pu;
        const selectedGroup = this.formGroup.get("groupFilter").value as PatientsGroup;
        this.dialog
          .open(AddToGroupDialogComponent, {
            data: { list, isDelete: true },
            disableClose: true,
          })
          .afterClosed()
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((res) => {
            if (res) {
              this.reloadGroups(selectedGroup);
              this.sessionService.needRefreshPatientDataList();
              this.resetSelection();
            }
          });
      },
      (error) => {
        FileLogger.error("PatientListPageComponent", "removeFromGroup", error);
      }
    );
  }

  private reloadGroups(group?: IPatientsGroup): Promise<void> {
    if (!this.userService.isAuthorizedSync(null, "groups", "GET")) {
      this.groups = [];
      this.selection.clear();
      this.groupId = null;
      this.formGroup.get("groupFilter").setValue(null);
      return;
    }
    let services: string[] = null;
    let current: string = null;
    if (this.userService.isMonitoringUser) {
      services = this.monitoringServices;
      current = this.sessionService.currentMonitoringService?.reference;
    } else {
      services = this.currentServices;
      current = this.sessionService.currentService?.reference;
    }
    if (!services || !current) {
      this.groups = [];
      this.selection.clear();
      this.groupId = null;
      this.formGroup.get("groupFilter").setValue(null);
      return;
    }
    return this.groupService
      .list(current, services)
      .pipe(first())
      .toPromise()
      .then((groups) => {
        this.groups = Tools.deepCopy(groups);
        groups.unshift(this.noGroup); // add the no-group option in order too display the select correctly
        group = groups.find((g) => g._id === group?._id);
        this.selection.clear();
        if (group) {
          this.groupId = group._id;
          this.formGroup.get("groupFilter").setValue(group);
        } else {
          this.groupId = null;
          this.formGroup.get("groupFilter").setValue(null);
        }
      });
  }

  public async sendCommunication(): Promise<void> {
    let patientUsers: IPatientUser[] = [];
    this.getPatientUsers(this.selection.selected).then(
      (pu: IPatientUser[]) => {
        patientUsers = pu;
        const selection = this.patientService.toReference(patientUsers);
        const group = this.formGroup.get("groupFilter")?.value;
        this.crud.createCommunication(selection, undefined, group); // allPatients undefined to let the modal loading all Patients.
      },
      (error) => {
        FileLogger.error("PatientListPageComponent", "sendCommunication", error);
      }
    );
  }

  public getPatientUsers(patients: IPatientsListInfo[]): Promise<IPatientUser[]> {
    const caremateIdentifiers = patients.map((patient) => patient.caremateIdentifier);
    return this.patientService.getPatientUsers(caremateIdentifiers).pipe(first()).toPromise();
  }

  public selectAllPatients(): void {
    this.patientService
      .getPatientListInfo({
        sortId: this.sort?.active,
        sortOrder: this.sort?.direction,
        // pageNumber: this.paginator.pageIndex,
        pageSize: this.patientsCount, // set patientsCount as a pageSize to get all patients now that pageSize is mandatory in backend.
        filters: this.filters,
        search: this.searchInput.nativeElement.value,
        withInactive: this.withInactive,
        groupId: this.groupId,
        services: this.currentServices,
        lang: this.sessionService.userLang,
        period: this.sessionService.globalPref?.inactifPatientDuration,
      })
      .pipe(first())
      .subscribe((list) => {
        this.selection.clear();
        this.selection.select(...list);
        this.forceSelected = true;
      });
  }

  public resetSelection(): void {
    this.selection.clear();
    this.actionOnMultiple = false;
    this.forceSelected = false;
    // remove 'select' column from displayedColumns
    const index = this.displayedColumns.findIndex((d) => d === "select");

    if (index !== -1) {
      this.displayedColumns.splice(index, 1);
    }
  }

  public compareGroupId(o1: PatientsGroup, o2: PatientsGroup): boolean {
    return o1?._id === o2?._id;
  }

  /**
   * Search for a patient using its ins number
   * @param insNumber the ins id of the patient
   * @returns
   */
  public searchViaIdentifierNumber(insNumber?: string): void {
    if (!insNumber) {
      this.computePatientsList();
    } else if (!this.formGroup.get("ins").errors) {
      this.computePatientsList(insNumber);
    }
  }

  public isExpanded(element?: IPatientsListInfo): boolean {
    if (!element) {
      return this.expandedElements.length === this.dataSource.getData().length;
    }
    return this.expandedElements.findIndex((e) => e.caremateIdentifier === element.caremateIdentifier) !== -1;
  }

  public onExpandRow(element: IPatientsListInfo): void {
    if (this.isExpanded(element)) {
      const index = this.expandedElements.findIndex((e) => e.caremateIdentifier === element.caremateIdentifier);
      if (index !== -1) {
        this.expandedElements.splice(index, 1);
        delete this.patientsObservations[element.caremateIdentifier];
      }
    } else {
      this.expandedElements.push(element);
      if (Tools.isDefined(this.patientDataPeriod)) {
        this.getPatientObservationsInfos([element.caremateIdentifier]);
      }
    }
    this.updatePreference();
  }

  public onExpandAll(): void {
    if (this.expandedElements.length) {
      this.expandedElements = [];
    } else {
      this.expandedElements = [...this.dataSource.getData()]; // create new array to avoid modifying the datasource
      if (Tools.isDefined(this.patientDataPeriod)) {
        this.getPatientObservationsInfos(this.expandedElements.map((e) => e.caremateIdentifier));
      }
    }
    this.updatePreference();
  }

  private getPatientObservationsInfos(caremateIds: string[]): void {
    if (Tools.isDefined(this.patientDataPeriod) && caremateIds?.length) {
      this.manageLoader(caremateIds, true);

      const BATCH_SIZE = 50;
      const batches: string[][] = [];

      // Split caremateIds into batches
      for (let i = 0; i < caremateIds.length; i += BATCH_SIZE) {
        batches.push(caremateIds.slice(i, i + BATCH_SIZE));
      }
      // Create an array of observables for each batch
      const batchObservables = batches.map((batch) =>
        this.observationsService.getPatientObservationsInfos(batch, this.patientDataPeriod, this.sessionService.userLang)
      );
      // Execute requests sequentially
      concat(...batchObservables)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (results) => {
            results.forEach((r) => {
              this.patientsObservations[r.subject] = {
                observations: r.observationsInfo,
                isLoading: false,
              };
            });
          },
          complete: () => {
            // need to set isLoading false for the patients with no observation found
            this.manageLoader(caremateIds, false);
          },
        });
    }
  }

  private manageLoader(caremateIds: string[], isLoading: boolean): void {
    caremateIds.forEach((id) => {
      if (this.patientsObservations[id]) {
        this.patientsObservations[id].isLoading = isLoading;
        if (isLoading) {
          // reset observations
          this.patientsObservations[id].observations = undefined;
        }
      } else {
        this.patientsObservations[id] = {
          isLoading: isLoading,
          observations: undefined,
        };
      }
    });
  }
  public async availablePatientDataPeriodsChanged(items: Item[]): Promise<void> {
    const item = items.find((i) => i.checked).value;
    this.patientDataPeriod = !item || isNaN(Number(item)) ? undefined : Number(item);

    if (Tools.isDefined(this.patientDataPeriod)) {
      // recalculate obs of expanded patient rows
      const caremateIds = Object.keys(this.patientsObservations);
      this.getPatientObservationsInfos(caremateIds);
    } else {
      this.expandedElements = [];
    }
    const element = [...this.dataSource.getData()];
    this.setPatientDataCount(element.map((e) => e.caremateIdentifier));
    this.setDisplayedColumns();
    await this.updatePreference();
  }

  private setupObservations() {
    this.availablePatientDataPeriods = [
      {
        value: undefined,
        display: "forms.null",
        checked: true,
      },
      {
        value: PatientDataInfoPeriod.TODAY.toString(),
        display: "patientDataInfoPeriod.TODAY",
        checked: false,
      },
      {
        value: PatientDataInfoPeriod.LAST_1_DAY.toString(),
        display: "patientDataInfoPeriod.LAST_1_DAY",
        checked: false,
      },
      {
        value: PatientDataInfoPeriod.LAST_2_DAYS.toString(),
        display: "patientDataInfoPeriod.LAST_2_DAYS",
        checked: false,
      },
      {
        value: PatientDataInfoPeriod.LAST_AVAILABLE.toString(),
        display: "patientDataInfoPeriod.LAST_AVAILABLE",
        checked: false,
      },
    ];

    if (!isNaN(Number(this.preferences?.patientDataPeriod))) {
      this.patientDataPeriod = Number(this.preferences.patientDataPeriod);
      const itemFound = this.availablePatientDataPeriods.find((ac) => ac.value === this.preferences.patientDataPeriod.toString());
      if (itemFound) {
        this.availablePatientDataPeriods.map((ac) => (ac.checked = false));
        itemFound.checked = true;

        if (this.preferences.patientOpenRows?.length) {
          const patients = this.dataSource.getData().filter((p) => this.preferences.patientOpenRows?.includes(p.caremateIdentifier));
          this.expandedElements.push(...patients);
          this.getPatientObservationsInfos(this.preferences.patientOpenRows);
        }
      }
    } else {
      this.patientDataPeriod = undefined;
    }
  }

  private setPatientDataCount(caremateIds: string[]): void {
    if (Tools.isDefined(this.patientDataPeriod) && this.patientDataPeriod !== PatientDataInfoPeriod.LAST_AVAILABLE && caremateIds?.length) {
      caremateIds.forEach((id) => {
        this.patientsNbObservations[id] = {
          isLoading: true,
          count: undefined,
        };
        this.patientsNbQuestionnaires[id] = {
          isLoading: true,
          count: undefined,
        };
      });
      const BATCH_SIZE = 50;
      const batches: string[][] = [];

      // Split caremateIds into batches
      for (let i = 0; i < caremateIds.length; i += BATCH_SIZE) {
        batches.push(caremateIds.slice(i, i + BATCH_SIZE));
      }
      if (
        this.displayedColumns.includes("nbObservations") &&
        this.userService.isAuthorizedSync(null, "dashboard/observationsCount", "GET")
      ) {
        const batchObservationsCountObservables = batches.map((batch) =>
          this.observationsService.getPatientObservationsCount(batch, this.patientDataPeriod)
        );
        // Execute requests sequentially
        concat(...batchObservationsCountObservables)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (results) => {
              results.forEach((r) => {
                this.patientsNbObservations[r.subject] = {
                  count: r.nbObservations,
                  isLoading: false,
                };
              });
            },
            complete: () => {
              caremateIds.forEach((id) => {
                if (this.patientsNbObservations[id].isLoading) {
                  this.patientsNbObservations[id] = {
                    isLoading: false,
                    count: 0,
                  };
                }
              });
            },
          });
      }
      if (
        this.displayedColumns.includes("nbQuestionnaires") &&
        this.userService.isAuthorizedSync(null, "dashboard/questionnairesCount", "GET")
      ) {
        const batchQuestionnairesCountObservables = batches.map((batch) =>
          this.qrService.getPatientQuestionnairesCount(batch, this.patientDataPeriod)
        );
        // Execute requests sequentially
        concat(...batchQuestionnairesCountObservables)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (results) => {
              results.forEach((r) => {
                this.patientsNbQuestionnaires[r.subject] = {
                  count: r.nbQuestionnaires,
                  isLoading: false,
                };
              });
            },
            complete: () => {
              caremateIds.forEach((id) => {
                if (this.patientsNbQuestionnaires[id].isLoading) {
                  this.patientsNbQuestionnaires[id] = {
                    isLoading: false,
                    count: 0,
                  };
                }
              });
            },
          });
      }
    } else {
      caremateIds.forEach((id) => {
        this.patientsNbQuestionnaires[id] = {
          isLoading: false,
          count: undefined,
        };
        this.patientsNbObservations[id] = {
          isLoading: false,
          count: undefined,
        };
      });
    }
  }
}
